import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { ApiError, createCampaign, decideCrisis, getCampaign, rotateCampaignAccess, sendCommand } from "./api";
import {
  activeCampaignId,
  listStoredCampaigns,
  parseRecoveryKey,
  recordCampaign,
  recoveryKeyFor,
  removeCampaignFromDevice,
  selectCampaign,
} from "./campaignStorage";
import type { BattleReport, CampaignPayload, GameCommand } from "../types";

export function preferNewerPayload(
  existing: CampaignPayload | undefined,
  incoming: CampaignPayload,
): CampaignPayload {
  if (!existing) return incoming;
  if (existing.campaign.revision !== incoming.campaign.revision) {
    return existing.campaign.revision > incoming.campaign.revision ? existing : incoming;
  }
  // Revisions now describe player decisions, not passive wall-clock syncs. A
  // poll and an idempotency replay can therefore share a revision while
  // carrying different simulation times; never let the older snapshot rewind
  // needs, tasks, expeditions, or a newly projected threat in the UI.
  return existing.state.game_time > incoming.state.game_time ? existing : incoming;
}

function isRevisionConflict(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === "revision_conflict";
}

export function campaignRefetchInterval(
  payload: CampaignPayload | undefined,
  mutationInFlight: boolean,
): number | false {
  if (mutationInFlight) return false;
  if (
    payload
    && (
      payload.crisis
      || payload.state.active_crisis
      || payload.state.community_status !== "holding"
    )
  ) return false;
  return 10_000;
}

export function useCampaign() {
  const queryClient = useQueryClient();
  const [campaignId, setCampaignId] = useState<string | null>(activeCampaignId);
  const [, setRegistryRevision] = useState(0);
  const [latestReport, setLatestReport] = useState<BattleReport | null>(null);
  const mutationInFlight = useRef(false);

  const storePayload = useCallback(
    (payload: CampaignPayload) => {
      const key = ["campaign", payload.campaign.id] as const;
      const existing = queryClient.getQueryData<CampaignPayload>(key);
      const preferred = preferNewerPayload(existing, payload);
      queryClient.setQueryData(key, preferred);
      if (preferred === payload && payload.battle_report) setLatestReport(payload.battle_report);
      return preferred;
    },
    [queryClient],
  );

  const query = useQuery({
    queryKey: ["campaign", campaignId],
    queryFn: async ({ signal }) => {
      const key = ["campaign", campaignId] as const;
      const cached = queryClient.getQueryData<CampaignPayload>(key);
      if (mutationInFlight.current && cached) return cached;
      const incoming = await getCampaign(campaignId!, signal);
      recordCampaign(incoming.campaign, { activate: true });
      return preferNewerPayload(
        queryClient.getQueryData<CampaignPayload>(key),
        incoming,
      );
    },
    enabled: Boolean(campaignId),
    refetchInterval: (result) => campaignRefetchInterval(
      result.state.data,
      mutationInFlight.current,
    ),
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const create = useMutation({
    mutationFn: ({ name, seed }: { name: string; seed?: number }) =>
      createCampaign(name, seed),
    onSuccess: (payload) => {
      recordCampaign(payload.campaign, { activate: true });
      setRegistryRevision((revision) => revision + 1);
      setCampaignId(payload.campaign.id);
      storePayload(payload);
    },
  });

  const command = useMutation({
    mutationFn: async (gameCommand: GameCommand) => {
      if (!campaignId) throw new Error("Campaign is not loaded.");
      const key = ["campaign", campaignId] as const;
      const idempotencyKey = crypto.randomUUID();
      mutationInFlight.current = true;
      try {
        await queryClient.cancelQueries({ queryKey: key });
        const payload = queryClient.getQueryData<CampaignPayload>(key);
        if (!payload) throw new Error("Campaign is not loaded.");
        try {
          return await sendCommand(payload, gameCommand, idempotencyKey);
        } catch (error) {
          if (!isRevisionConflict(error)) throw error;
          storePayload(await getCampaign(campaignId));
          throw new ApiError(
            "This save changed on another device. Review the updated bunker and issue the order again.",
            "stale_decision",
            409,
          );
        }
      } finally {
        mutationInFlight.current = false;
      }
    },
    onSuccess: storePayload,
    onError: () => void query.refetch(),
  });

  const crisis = useMutation({
    mutationFn: async (decision: "accept" | "take_command") => {
      if (!campaignId) throw new Error("Campaign is not loaded.");
      const key = ["campaign", campaignId] as const;
      const idempotencyKey = crypto.randomUUID();
      mutationInFlight.current = true;
      try {
        await queryClient.cancelQueries({ queryKey: key });
        const payload = queryClient.getQueryData<CampaignPayload>(key);
        if (!payload) throw new Error("Campaign is not loaded.");
        try {
          return await decideCrisis(payload, decision, idempotencyKey);
        } catch (error) {
          if (!isRevisionConflict(error)) throw error;
          storePayload(await getCampaign(campaignId));
          throw new ApiError(
            "This crisis changed on another device. Review the current timeline before deciding again.",
            "stale_decision",
            409,
          );
        }
      } finally {
        mutationInFlight.current = false;
      }
    },
    onSuccess: storePayload,
    onError: () => void query.refetch(),
  });

  const recover = useMutation({
    mutationFn: async (value: string) => {
      const parsed = parseRecoveryKey(value);
      if (!parsed) {
        throw new ApiError(
          "That recovery key is not valid.",
          "invalid_recovery_key",
          400,
        );
      }
      const payload = await getCampaign(
        parsed.campaignId,
        undefined,
        parsed.accessKey,
      );
      recordCampaign(payload.campaign, {
        activate: true,
        accessKey: parsed.accessKey,
      });
      return payload;
    },
    onSuccess: (payload) => {
      setRegistryRevision((revision) => revision + 1);
      setCampaignId(payload.campaign.id);
      storePayload(payload);
    },
  });

  const rotateAccess = useMutation({
    mutationFn: async (id: string) => {
      mutationInFlight.current = true;
      try {
        return await rotateCampaignAccess(id);
      } finally {
        mutationInFlight.current = false;
      }
    },
    onSuccess: (payload) => {
      // The server reveals the replacement capability once. Persist the new
      // payload in a single registry write before exposing the formatted key;
      // the revoked capability is never kept as a fallback.
      recordCampaign(payload.campaign, { activate: payload.campaign.id === campaignId });
      setRegistryRevision((revision) => revision + 1);
      storePayload(payload);
    },
  });

  const activate = useCallback((id: string) => {
    selectCampaign(id);
    setRegistryRevision((revision) => revision + 1);
    setLatestReport(null);
    setCampaignId(id);
  }, []);

  const reset = useCallback(() => {
    selectCampaign(null);
    setRegistryRevision((revision) => revision + 1);
    setCampaignId(null);
    setLatestReport(null);
  }, []);

  const forget = useCallback(
    (id: string) => {
      removeCampaignFromDevice(id);
      queryClient.removeQueries({ queryKey: ["campaign", id] });
      setRegistryRevision((revision) => revision + 1);
      if (campaignId === id) {
        setCampaignId(null);
        setLatestReport(null);
      }
    },
    [campaignId, queryClient],
  );

  return {
    campaignId,
    payload: query.data,
    isLoading: query.isLoading,
    error: query.error ?? create.error ?? command.error ?? crisis.error ?? recover.error ?? rotateAccess.error,
    isBusy: create.isPending || command.isPending || crisis.isPending || recover.isPending || rotateAccess.isPending,
    create: async (variables: { name: string; seed?: number }) => {
      try {
        return await create.mutateAsync(variables);
      } catch {
        return undefined;
      }
    },
    command: async (gameCommand: GameCommand) => {
      try {
        return await command.mutateAsync(gameCommand);
      } catch {
        return undefined;
      }
    },
    decideCrisis: async (decision: "accept" | "take_command") => {
      try {
        return await crisis.mutateAsync(decision);
      } catch {
        return undefined;
      }
    },
    reset,
    activate,
    forget,
    recover: async (value: string) => {
      try {
        return await recover.mutateAsync(value);
      } catch {
        return undefined;
      }
    },
    rotateAccess: async (id: string) => {
      try {
        await rotateAccess.mutateAsync(id);
        return recoveryKeyFor(id);
      } catch {
        return null;
      }
    },
    saves: listStoredCampaigns(),
    recoveryKey: campaignId ? recoveryKeyFor(campaignId) : null,
    recoveryKeyFor,
    refetch: query.refetch,
    latestReport,
    dismissReport: () => setLatestReport(null),
  };
}
